import React, { useEffect, useMemo, useState } from 'react';
import { collection } from 'firebase/firestore';
import { db } from '../../../../firebase-client';
import { safeOnSnapshot } from '../../../../utils/firestoreSubscriptions';
import EditButton from '../../../ui/EditButton';
import { safeSailingBoatLabel } from '../../utils/sailingFleet';

export default function ClubBoatsCard({
  isAdmin,
  teamId,
  modules = {},
  onEdit,
  onEditBoat,
  canManageMaintenance = false,
  onCreateMaintenanceTicket,
}) {
  const enabled = Boolean(modules?.sailing?.boats?.enabled);
  const [boats, setBoats] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAdmin || !enabled || !teamId) {
      setBoats([]);
      return undefined;
    }
    setLoading(true);
    const unsubscribe = safeOnSnapshot(collection(db, 'teams', teamId, 'sailingBoats'), (snapshot) => {
      setBoats(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      setLoading(false);
    }, () => setLoading(false), { label: 'sailing-fleet', teamId });
    return () => unsubscribe();
  }, [enabled, isAdmin, teamId]);

  const summary = useMemo(() => boats.reduce((acc, boat) => {
    const status = String(boat.status || 'active');
    if (status === 'maintenance') acc.maintenance += 1;
    else if (status === 'retired') acc.retired += 1;
    else acc.active += 1;
    return acc;
  }, { active: 0, maintenance: 0, retired: 0 }), [boats]);

  if (!isAdmin || !enabled) return null;

  return (
    <div className="team-card">
      <div className="card-header-with-icon">
        <span className="card-title">Club Boats</span>
        <EditButton section="sailingBoats" onEdit={onEdit} />
      </div>
      <div className="card-body">
        <div className="availability-status-list">
          <div className="availability-status-item"><span>Active</span><span className="availability-pill available">{loading ? '…' : summary.active}</span></div>
          <div className="availability-status-item"><span>Maintenance</span><span className="availability-pill unavailable">{summary.maintenance}</span></div>
          <div className="availability-status-item"><span>Total</span><span className="availability-pill info">{boats.length}</span></div>
        </div>

        {boats.length === 0 ? (
          <p className="text-muted" style={{ marginTop: 12 }}>No club-owned sailing boats recorded yet.</p>
        ) : (
          <div style={{ display: 'grid', gap: 8, marginTop: 12, maxHeight: 260, overflow: 'auto' }}>
            {boats.slice(0, 8).map((boat) => (
              <div key={boat.id} className="list-row" style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                <span>
                  <strong>{safeSailingBoatLabel(boat)}</strong>
                  <small className="text-muted" style={{ display: 'block' }}>
                    {[boat.sailNumber ? `Sail ${boat.sailNumber}` : '', boat.length ? `${boat.length}m` : '', boat.currentMooringSpaceId ? 'Mooring linked' : 'No mooring'].filter(Boolean).join(' · ')}
                  </small>
                </span>
                <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {onEditBoat && <button type="button" className="btn btn--secondary btn--xs" onClick={() => onEditBoat(boat)}>Edit</button>}
                  {canManageMaintenance && onCreateMaintenanceTicket && <button type="button" className="btn btn--secondary btn--xs" onClick={() => onCreateMaintenanceTicket(boat)}>
                    Create maintenance ticket
                  </button>}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
